import { formatProductName } from "@/utils/formatUtils";
import { treeNodeFilterFunction } from "@/utils/formUtils";
import { DataNode } from "@rc-component/tree-select/es/interface";
import { Button, Divider, Space, TreeSelect } from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEnumeration } from "../../../../common/api/queries";
import type { UUID } from "../../../../typings/global";
import { useIsCurrentAgentTypePlus } from "../../../auth/hooks";
import { EnumerationType } from "../../enums";

const groupFilter = (value: string) => value.startsWith("group-");

interface Props {
  value?: UUID[];
  onChange?: (value?: UUID[]) => void;
  allowClear?: boolean;
  institutionIdsFilter?: UUID[];
}

export const ProductTreeSelect = ({ value, onChange, allowClear = true, institutionIdsFilter }: Props) => {
  const { t } = useTranslation();

  const isCurrentAgentTypePlus = useIsCurrentAgentTypePlus();
  const productGroupsEnums = useEnumeration(EnumerationType.PRODUCT_GROUPS);
  const productEnumerations = useEnumeration(EnumerationType.PRODUCTS);

  const productEnums = useMemo(
    () =>
      productEnumerations.filter(
        (product) =>
          !institutionIdsFilter ||
          institutionIdsFilter.length === 0 ||
          institutionIdsFilter.some((institutionId) => product.institutionIds.includes(institutionId)),
      ),
    [productEnumerations, institutionIdsFilter],
  );

  useEffect(() => {
    onChange?.(value?.filter((productId) => productEnums.some((product) => product.id === productId)));
  }, [productEnums]);

  const productsTree = useMemo(
    () =>
      productGroupsEnums
        .filter((productGroup) => productEnums.some((product) => product.productGroupId === productGroup.id))
        .map<DataNode>((productGroup) => ({
          value: `group-${productGroup.id}`,
          title: productGroup.name,
          children: productEnums
            .filter((product) => product.productGroupId === productGroup.id)
            .map<DataNode>((product) => ({
              value: product.id,
              title: formatProductName(product),
            })),
          disabled: productEnums.filter((product) => product.productGroupId === productGroup.id).length === 0,
        })),
    [productGroupsEnums, productEnums],
  );

  const coveredByNbsProducts = productEnums.filter((product) => product.coveredByNbs);
  const nonCoveredByNbsProducts = productEnums.filter((product) => !product.coveredByNbs);

  const shouldShowExtendedButtons = !!coveredByNbsProducts.length && !!nonCoveredByNbsProducts.length;

  return (
    <TreeSelect
      showCheckedStrategy={TreeSelect.SHOW_PARENT}
      treeCheckable
      maxTagCount="responsive"
      popupMatchSelectWidth={false}
      allowClear={allowClear}
      showSearch={{ filterTreeNode: treeNodeFilterFunction }}
      treeData={productsTree}
      popupRender={(menu) => (
        <>
          {menu}
          {shouldShowExtendedButtons ? (
            <>
              <Divider style={{ margin: "4px 0" }} />

              <Space style={{ marginTop: 8, justifyContent: "center", width: "100%" }}>
                <Button
                  type="text"
                  size="small"
                  onClick={() => onChange?.(coveredByNbsProducts.map((product) => product.id))}
                >
                  {isCurrentAgentTypePlus
                    ? t("product.helpers.productsProfiPlus")
                    : t("product.helpers.productsCoveredByNbs")}
                </Button>
                <Divider orientation="vertical" style={{ margin: "0 2px" }} />
                <Button
                  type="text"
                  size="small"
                  onClick={() => onChange?.(nonCoveredByNbsProducts.map((product) => product.id))}
                >
                  {isCurrentAgentTypePlus
                    ? t("product.helpers.productsProjectsPlus")
                    : t("product.helpers.productsNoCoveredByNbs")}
                </Button>
              </Space>
            </>
          ) : undefined}
        </>
      )}
      value={value?.filter((productId) => productEnums.some((product) => product.id === productId))}
      onChange={(data) => {
        if (!data) {
          return;
        }

        const productGroupValues = data
          .filter(groupFilter)
          .map((value) => value.split("group-")[1])
          .filter((value): value is string => !!value);

        const productGroupProducts = productEnums
          .filter((product) => productGroupValues.includes(product.productGroupId))
          .map((product) => product.id);

        const productValues = data.filter((value) => !groupFilter(value));

        const result = [...productValues, ...productGroupProducts];

        onChange?.(result);
      }}
    />
  );
};
